import React, { useEffect, useState } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import haptics from '@/lib/haptics';

const DISMISS_KEY = 'apexflow.pwa.installDismissedAt';
const SUPPRESS_DAYS = 7;

/**
 * PWAInstallPrompt — captures the `beforeinstallprompt` event (Chrome /
 * Edge / Samsung Internet) and renders a small bottom-pinned banner
 * inviting the user to install the app to their home screen.
 *
 * On Safari (no programmatic prompt) we instead show iOS-specific
 * instructions ("Tap ⎙ then 'Add to Home Screen'") if the user is on
 * iOS Safari and not already in standalone mode.
 *
 * Dismissals are remembered for SUPPRESS_DAYS so we don't nag.
 */
export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    // Already installed → bail.
    if (window.matchMedia?.('(display-mode: standalone)').matches) return;
    if (window.navigator?.standalone) return;

    // Recently dismissed → bail.
    try {
      const last = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (last && Date.now() - last < SUPPRESS_DAYS * 86400_000) return;
    } catch (_) { /* */ }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    // iOS Safari fallback (no install API).
    const ua = navigator.userAgent || '';
    const isIos = /iPhone|iPad|iPod/.test(ua) && !/CriOS|FxiOS/.test(ua);
    if (isIos) setShowIosHint(true);

    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); }
    catch (_) { /* */ }
    haptics.dismiss();
    setDeferred(null);
    setShowIosHint(false);
  };

  const accept = async () => {
    if (!deferred) return;
    haptics.tick();
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      if (outcome === 'accepted') haptics.confirm();
    } catch (_) { /* */ }
    setDeferred(null);
  };

  if (!deferred && !showIosHint) return null;

  return (
    <div data-testid="pwa-install-prompt"
         style={{
           position: 'fixed', left: 12, right: 12, bottom: 76, zIndex: 9600,
           background: '#0a0a0a',
           border: '1px solid var(--accent-amber)',
           borderRadius: 8,
           boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
           padding: 12,
           display: 'flex', alignItems: 'center', gap: 12,
         }}>
      <Smartphone size={22} style={{ color: 'var(--accent-amber)',
                                     flexShrink: 0 }} />
      <div style={{ flex: 1, fontSize: 12, lineHeight: 1.4 }}>
        {deferred ? (
          <>
            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
              Install ApexFlow
            </div>
            <div style={{ color: 'var(--text-secondary)' }}>
              Faster launch, fullscreen, offline-shell support.
            </div>
          </>
        ) : (
          <>
            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
              Add to Home Screen
            </div>
            <div style={{ color: 'var(--text-secondary)' }}>
              Tap <b>⎙ Share</b> then <b>"Add to Home Screen"</b>.
            </div>
          </>
        )}
      </div>
      {deferred && (
        <button onClick={accept}
                data-testid="pwa-install-accept"
                className="btn-sharp"
                style={{ background: 'var(--accent-amber)',
                         color: '#0a0a0a', border: 'none',
                         padding: '6px 12px', fontSize: 11,
                         fontWeight: 700, whiteSpace: 'nowrap',
                         display: 'inline-flex', alignItems: 'center',
                         gap: 4 }}>
          <Download size={11} /> INSTALL
        </button>
      )}
      <button onClick={dismiss}
              data-testid="pwa-install-dismiss"
              aria-label="Dismiss"
              style={{ background: 'transparent', border: 'none',
                       color: 'var(--text-muted)', cursor: 'pointer',
                       padding: 4 }}>
        <X size={14} />
      </button>
    </div>
  );
}
